import { useState } from "react";
import { X } from "lucide-react";

/** Row-selection state for admin tables (CR14 bulk actions). */
export function useSelection() {
  const [sel, setSel] = useState(() => new Set());
  const toggle = (key) =>
    setSel((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const setAll = (keys, on) =>
    setSel((cur) => {
      const next = new Set(cur);
      for (const k of keys) {
        if (on) next.add(k);
        else next.delete(k);
      }
      return next;
    });
  const clear = () => setSel(new Set());
  return { sel, toggle, setAll, clear, count: sel.size };
}

export function SelectAllBox({ checked, indeterminate, onChange, testid }) {
  return (
    <input
      type="checkbox"
      data-testid={testid}
      checked={checked}
      ref={(el) => { if (el) el.indeterminate = indeterminate; }}
      onChange={(e) => onChange(e.target.checked)}
      className="h-3.5 w-3.5 accent-[#00FF87]"
    />
  );
}

export function RowCheckbox({ selected, onToggle, testid }) {
  return (
    <input
      type="checkbox"
      data-testid={testid}
      checked={selected}
      onChange={onToggle}
      className="h-3.5 w-3.5 shrink-0 accent-[#00FF87]"
    />
  );
}

/** Floating bar shown when rows are selected: bulk actions + optional protected purge. */
export default function BulkBar({ count, onClear, actions, onPurge, t }) {
  if (count === 0 && !onPurge) return null;
  return (
    <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-[#00FF87]/40 bg-[#00FF87]/5 px-3 py-2" data-testid="bulk-bar">
      <span className="font-mono2 text-[10px] font-bold uppercase tracking-wider text-[#00FF87]" data-testid="bulk-count">
        {t("admin.bulk_selected", { n: count })}
      </span>
      {actions.map((a) => (
        <button
          key={a.key}
          data-testid={`bulk-${a.key}`}
          onClick={a.onClick}
          disabled={count === 0}
          className={`rounded-md border px-2.5 py-1 font-mono2 text-[10px] font-bold uppercase tracking-wider disabled:opacity-40 ${a.cls}`}
        >
          {a.label}
        </button>
      ))}
      {onPurge && (
        <button
          data-testid="bulk-purge"
          onClick={onPurge}
          className="rounded-md border border-red-500/50 px-2.5 py-1 font-mono2 text-[10px] font-bold uppercase tracking-wider text-red-400 hover:bg-red-500/10"
        >
          {t("admin.purge")}
        </button>
      )}
      <button data-testid="bulk-clear" onClick={onClear} className="ml-auto text-slate-500 hover:text-slate-200">
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/** Protected purge: the admin must type DELETE to arm the confirm button. */
export function PurgeDialog({ open, title, hint, busy, onClose, onConfirm, t }) {
  const [value, setValue] = useState("");
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="purge-dialog">
      <div className="hud-card w-full max-w-sm rounded-xl p-5">
        <h3 className="font-display text-lg font-bold uppercase tracking-tight text-red-400">{title}</h3>
        <p className="mt-1 mb-4 text-xs leading-relaxed text-slate-400">{hint}</p>
        <input
          data-testid="purge-confirm-input"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="DELETE"
          className="mb-4 w-full rounded-md border border-red-500/40 bg-[#0B0E14] px-3 py-2 font-mono2 text-sm tracking-widest text-red-300 focus:border-red-500 focus:outline-none"
        />
        <div className="flex justify-end gap-2">
          <button
            data-testid="purge-cancel"
            onClick={onClose}
            className="rounded-md border border-[#1F293D] px-3 py-1.5 font-mono2 text-[10px] uppercase tracking-wider text-slate-400 hover:text-slate-200"
          >
            {t("common.cancel")}
          </button>
          <button
            data-testid="purge-confirm-button"
            onClick={onConfirm}
            disabled={value !== "DELETE" || busy}
            className="rounded-md border border-red-500/60 bg-red-500/15 px-3 py-1.5 font-mono2 text-[10px] font-bold uppercase tracking-wider text-red-400 hover:bg-red-500/25 disabled:opacity-40"
          >
            {busy ? t("common.loading") : t("admin.purge")}
          </button>
        </div>
      </div>
    </div>
  );
}
